import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getStoredConsent,
  saveAcceptAll,
  saveNecessaryOnly,
  saveConsent,
} from '../services/cookieConsentService';
import { initializeAnalytics, updateAnalyticsConsent } from '../services/analyticsService';

const CookieConsentContext = createContext(null);

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = getStoredConsent();
      if (stored) {
        setConsent(stored);
        setIsBannerVisible(false);
        initializeAnalytics(Boolean(stored.analytics));
      } else {
        setIsBannerVisible(true);
        // 初回（未同意時）は同意モード: analytics_storage: 'denied' で初期化
        initializeAnalytics(false);
      }
    } catch (error) {
      console.error('Error in CookieConsentProvider initial load:', error);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const newConsent = saveAcceptAll();
    if (newConsent) {
      setConsent(newConsent);
      setIsBannerVisible(false);
      setIsModalOpen(false);
      updateAnalyticsConsent(true);
    }
  }, []);

  const handleNecessaryOnly = useCallback(() => {
    const newConsent = saveNecessaryOnly();
    if (newConsent) {
      setConsent(newConsent);
      setIsBannerVisible(false);
      setIsModalOpen(false);
      updateAnalyticsConsent(false);
    }
  }, []);

  const handleSaveSettings = useCallback((updatedPartial) => {
    const newConsent = saveConsent(updatedPartial);
    if (newConsent) {
      setConsent(newConsent);
      setIsBannerVisible(false);
      setIsModalOpen(false);
      updateAnalyticsConsent(Boolean(newConsent.analytics));
    }
  }, []);

  const openSettingsModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        isBannerVisible,
        isModalOpen,
        acceptAll: handleAcceptAll,
        acceptNecessaryOnly: handleNecessaryOnly,
        saveSettings: handleSaveSettings,
        openSettingsModal,
        closeSettingsModal,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
